import { ErrorCodes } from '@tableau/api-internal-contract-js';
import { InternalTableauError } from '../InternalTableauError';

/**
 * Simple registry class which creates a mapping based on two keys.
 *
 * @export
 * @class MappingRegistry
 * @template TInputEnumType
 * @template TOutputEnumType
 * @template TMappingStorageType
 */
export class MappingRegistry<TInputEnumType extends string, TOutputEnumType extends string, TMappingStorageType> {
  private _registry: { [key: string]: TMappingStorageType };

  public constructor() {
    this._registry = {};
  }

  public has(inputType: TInputEnumType, outputType: TOutputEnumType): boolean {
    const key = this.makeKey(inputType, outputType);
    if (!this._registry.hasOwnProperty(key)) {
      return false;
    }

    if (!this._registry[key]) {
      return false;
    }

    return true;
  }

  public get(inputType: TInputEnumType, outputType: TOutputEnumType): TMappingStorageType {
    if (!this.has(inputType, outputType)) {
      throw new InternalTableauError(ErrorCodes.MISSING_ENUM_MAPPING, `Missing requested mapping: ${inputType} to ${outputType}`);
    }

    const key = this.makeKey(inputType, outputType);
    return this._registry[key];
  }

  public addRegistration(inputType: TInputEnumType, outputType: TOutputEnumType, storageItem: TMappingStorageType): void {
    const key = this.makeKey(inputType, outputType);

    // Add this item
    this._registry[key] = storageItem;
  }

  /**
   * Combines the two keys into a unique string
   *
   * @private
   * @param {TInputEnumType} inputType
   * @param {TOutputEnumType} outputType
   * @returns {string}
   * @memberof MappingRegistry
   */
  private makeKey(inputType: TInputEnumType, outputType: TOutputEnumType): string {
    const keyObj = { input: inputType, output: outputType };
    return JSON.stringify(keyObj);
  }
}
